// Detail publikovaného Fokus seznamu — verdikty, model + scouting, hlasování, CZ/EN.
import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import AppShell from "../components/AppShell";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";
import { focusListPublicGet, focusVote, focusVotes, focusListTranslate } from "../lib/focus-api";

const I18N = {
    cs: {
        back: "Fokus seznamy", curated: "Sestavil", rep: "reputace", resolved: "vyhodnoceno", hits: "trefených picků",
        resolution: "Vyhodnocení po 3 GW", hitLbl: "TREFA", missLbl: "VEDLE", ptsDelta: "b", priceDelta: "cena",
        myVoteOk: "Tvůj hlas byl správný — +5 reputace", myVoteMiss: "Tvůj hlas nevyšel", voteClosed: "Hlasování vyhodnoceno",
        explain: (m, v) => <>Hlasuj u každého hráče, jestli je to dobrý pick. Když se za 3 gameweeky ukáže, že jsi měl pravdu, získáš <span className="text-fa-mint font-bold">+5 reputace</span>.</>,
        goodPick: "Tvůj verdikt?", votesLbl: "hlasů", community: "komunita",
        loginToast: "Pro hlasování se přihlas", voteOk: "Verdikt uložen — když se trefíš, získáš +5 reputace", voteFail: "Hlasování selhalo",
        model: (w) => `Model GW okno ${w}`, own: "Ownership", pts: "Body 25/26", mins: "Minuty", scout: "Scout rating", trend: "Trend", rot: "Riziko rotace",
        role: "Role", signal: "Poslední signál", newCoach: "Nový trenér", notFound: "Seznam nenalezen.", players: "hráčů",
        statuses: { SELECTED: "Hlavní volba", FINALIST: "Finalista", ALTERNATIVE: "Alternativa" },
        footer: "Data: FPL model Fantasy Academy (projekce bodů, minutáž, FDR) + preseason scouting databáze. Snapshot k datu publikace.",
    },
    en: {
        back: "Focus lists", curated: "Curated by", rep: "reputation", resolved: "resolved", hits: "correct picks",
        resolution: "Resolved after 3 GWs", hitLbl: "HIT", missLbl: "MISS", ptsDelta: "pts", priceDelta: "price",
        myVoteOk: "Your vote was correct — +5 reputation", myVoteMiss: "Your vote missed", voteClosed: "Voting resolved",
        explain: () => <>Vote on each player — is it a good pick? If you're proven right after 3 gameweeks, you earn <span className="text-fa-mint font-bold">+5 reputation</span>.</>,
        goodPick: "Your verdict?", votesLbl: "votes", community: "community",
        loginToast: "Sign in to vote", voteOk: "Verdict saved — you earn +5 reputation if you're right", voteFail: "Voting failed",
        model: (w) => `Model, next ${w} GWs`, own: "Ownership", pts: "Points 25/26", mins: "Minutes", scout: "Scout rating", trend: "Trend", rot: "Rotation risk",
        role: "Role", signal: "Latest signal", newCoach: "New coach", notFound: "List not found.", players: "players",
        statuses: { SELECTED: "Top pick", FINALIST: "Finalist", ALTERNATIVE: "Alternative" },
        footer: "Data: Fantasy Academy FPL model (points projection, minutes, FDR) + preseason scouting database. Snapshot as of publication.",
    },
};

const VERDICT_STYLE = {
    buy: "bg-fa-mint text-black", watch: "bg-fa-yellow text-black", avoid: "bg-red-400 text-black",
};

const Stat = ({ label, value, accent }) => (value == null || value === "" ? null : (
    <div className="min-w-[72px]">
        <div className="font-mono text-[7px] uppercase tracking-widest text-white/35">{label}</div>
        <div className={`font-mono text-[11px] font-bold ${accent || "text-white/85"}`}>{value}</div>
    </div>
));

const VOTE_OPTS = [
    ["buy", "BUY", "border-fa-mint bg-fa-mint/15 text-fa-mint", "bg-fa-mint"],
    ["watch", "WATCH", "border-fa-yellow bg-fa-yellow/15 text-fa-yellow", "bg-fa-yellow"],
    ["avoid", "AVOID", "border-red-400 bg-red-400/15 text-red-300", "bg-red-400"],
];

function VotePanel({ slug, p, agg, onVoted, T }) {
    const { user, login } = useAuth();
    const a = agg?.[String(p.fpl_id)] || { buy: 0, watch: 0, avoid: 0, my_vote: null };
    const total = (a.buy || 0) + (a.watch || 0) + (a.avoid || 0);
    const myRes = a.my_result;

    const cast = async (vote) => {
        if (myRes) return;
        if (!user) { toast.info(T.loginToast); login(); return; }
        try {
            await focusVote(slug, p.fpl_id, vote);
            toast.success(T.voteOk);
            onVoted();
        } catch (e) { toast.error(e.response?.data?.detail || T.voteFail); }
    };

    return (
        <div className="pt-2.5 border-t border-white/5 space-y-1.5" data-testid={`focus-vote-panel-${p.fpl_id}`}>
            <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-[8px] uppercase tracking-widest text-white/35">{T.goodPick}</span>
                {VOTE_OPTS.map(([k, label, act]) => (
                    <button key={k} type="button" onClick={() => cast(k)} data-testid={`focus-vote-${k}-${p.fpl_id}`}
                            className={`font-mono text-[9px] uppercase tracking-widest px-2.5 py-1 border font-bold transition-colors ${a.my_vote === k ? act : "border-white/15 text-white/50 hover:text-white hover:border-white/40"}`}>
                        {label} {a[k] || 0}
                    </button>
                ))}
                {total > 0 && (
                    <span className="ml-auto font-mono text-[9px] text-white/45" data-testid={`focus-vote-total-${p.fpl_id}`}>
                        {total} {T.votesLbl}
                    </span>
                )}
            </div>
            {total > 0 && (
                <div className="h-1 flex overflow-hidden bg-white/5" data-testid={`focus-vote-bar-${p.fpl_id}`}>
                    {VOTE_OPTS.map(([k, , , barCls]) => (
                        <div key={k} className={barCls} style={{ width: `${((a[k] || 0) / total) * 100}%` }} />
                    ))}
                </div>
            )}
            {myRes && (
                <div className={`font-mono text-[9px] flex items-center gap-1.5 ${myRes.correct ? "text-fa-mint" : "text-red-300"}`}
                     data-testid={`focus-my-vote-result-${p.fpl_id}`}>
                    {myRes.correct ? "✓" : "✗"} {myRes.correct ? T.myVoteOk : T.myVoteMiss}
                    {myRes.delta_points != null && <span className="text-white/45">· {myRes.delta_points > 0 ? "+" : ""}{myRes.delta_points} {T.ptsDelta}</span>}
                </div>
            )}
        </div>
    );
}

const STATUS_BADGE = {
    SELECTED: "bg-fa-mint text-black",
    FINALIST: "border border-fa-yellow text-fa-yellow bg-fa-yellow/10",
    ALTERNATIVE: "border border-white/40 text-white/80",
};
const CARD_BORDER = {
    SELECTED: "border-fa-mint/60 bg-fa-mint/[0.04]",
    FINALIST: "border-fa-yellow/40",
};

function PlayerCard({ slug, p, rank, agg, onVoted, T, note, res }) {
    const s = p.scouting || {};
    const st = p.candidate_status;
    return (
        <div className={`border p-4 space-y-3 ${CARD_BORDER[st] || "border-white/10"} bg-black/40`} data-testid={`focus-player-${p.fpl_id}`}>
            <div className="flex items-center gap-2 flex-wrap">
                <span className="font-display font-black text-fa-yellow text-lg w-7">#{rank}</span>
                {p.player_key ? (
                    <Link to={`/players/${p.player_key}`} className="font-display font-black uppercase text-base hover:text-fa-mint transition-colors">{p.name}</Link>
                ) : (
                    <span className="font-display font-black uppercase text-base">{p.name}</span>
                )}
                {T.statuses[st] && (
                    <span className={`font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 font-black ${STATUS_BADGE[st]}`} data-testid={`focus-status-${p.fpl_id}`}>
                        {st === "SELECTED" ? "★ " : ""}{T.statuses[st]}
                    </span>
                )}
                {p.verdict && (
                    <span className={`font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 font-black ${VERDICT_STYLE[p.verdict] || ""}`} data-testid={`focus-verdict-${p.fpl_id}`}>
                        {p.verdict.toUpperCase()}
                    </span>
                )}
                <span className="font-mono text-[9px] text-white/50">{p.team} · {p.position}</span>
                <span className="ml-auto font-mono text-sm font-bold text-fa-mint">£{p.price}</span>
            </div>
            <div className="flex gap-4 flex-wrap">
                <Stat label={T.model(p.pred_window || 4)} value={p.predicted_total != null ? `${p.predicted_total} b` : null} accent="text-fa-mint" />
                <Stat label={T.own} value={`${p.ownership_pct}%`} />
                <Stat label={T.pts} value={p.total_points} />
                <Stat label={T.mins} value={p.minutes} />
                <Stat label={T.scout} value={s.preseason_rating != null ? `${s.preseason_rating}/10` : null} accent="text-fa-yellow" />
                <Stat label={T.trend} value={s.trend} accent="text-fa-sky" />
                <Stat label={T.rot} value={s.rotation_risk} />
            </div>
            {(s.tactical_role || s.latest_change) && (
                <p className="font-mono text-[10px] text-white/55 leading-relaxed">
                    {s.tactical_role && <><span className="text-white/35 uppercase text-[8px] tracking-widest">{T.role} </span>{s.tactical_role}. </>}
                    {s.latest_change && <><span className="text-white/35 uppercase text-[8px] tracking-widest">{T.signal} </span>{s.latest_change}.</>}
                </p>
            )}
            {p.new_coach && <span className="inline-block font-mono text-[8px] uppercase tracking-widest px-1.5 py-0.5 border border-fa-sky/50 text-fa-sky">{T.newCoach}</span>}
            {note && (
                <p className="text-white/80 text-sm leading-relaxed border-l-2 border-fa-yellow pl-3" data-testid={`focus-player-note-${p.fpl_id}`}>{note}</p>
            )}
            {res && (
                <div className={`border px-3 py-2 font-mono text-[10px] flex items-center gap-2.5 flex-wrap ${res.hit ? "border-fa-mint/40 bg-fa-mint/5" : "border-red-400/40 bg-red-400/5"}`}
                     data-testid={`focus-resolution-${p.fpl_id}`}>
                    <b className={res.hit ? "text-fa-mint" : "text-red-300"}>{res.hit ? `✓ ${T.hitLbl}` : `✗ ${T.missLbl}`}</b>
                    <span className="text-white/60 uppercase text-[8px] tracking-widest">{T.resolution}</span>
                    <span className="text-white/85 font-bold">{res.delta_points > 0 ? "+" : ""}{res.delta_points} {T.ptsDelta}</span>
                    {res.delta_cost != null && (
                        <span className="text-white/55">{T.priceDelta} {res.delta_cost > 0 ? "+" : ""}£{(res.delta_cost / 10).toFixed(1)}</span>
                    )}
                </div>
            )}
            <VotePanel slug={slug} p={p} agg={agg} onVoted={onVoted} T={T} />
        </div>
    );
}

export default function FocusListDetail() {
    const { slug } = useParams();
    const { lang } = useLang();
    const [list, setList] = useState(null);
    const [votes, setVotes] = useState({});
    const [tr, setTr] = useState(null);
    const [err, setErr] = useState(null);
    const T = I18N[lang] || I18N.cs;
    const en = lang === "en";

    const loadVotes = useCallback(() => {
        focusVotes(slug).then((d) => setVotes(d.votes || {})).catch(() => {});
    }, [slug]);

    useEffect(() => {
        focusListPublicGet(slug).then((d) => {
            setList(d);
            document.title = `${d.title} | Fantasy Academy`;
        }).catch(() => setErr(T.notFound));
        loadVotes();
    }, [slug, loadVotes]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (en && list && !tr) focusListTranslate(slug).then(setTr).catch(() => {});
    }, [en, list, tr, slug]);

    const title = en && tr ? tr.title : list?.title;
    const intro = en && tr ? tr.intro : list?.intro;
    const noteFor = (p) => (en && tr?.notes?.[String(p.fpl_id)]) || p.note;

    return (
        <AppShell>
            <div className="max-w-3xl mx-auto space-y-4" data-testid="focus-list-detail-page">
                <Link to="/fokus" className="font-mono text-[10px] uppercase tracking-widest text-white/50 hover:text-white inline-flex items-center gap-1">
                    <ArrowLeft size={11} /> {T.back}
                </Link>
                {err && <p className="font-mono text-[11px] text-red-300">{err}</p>}
                {list && (
                    <article className="space-y-4">
                        <div className="font-mono text-[9px] uppercase tracking-widest text-white/40">
                            {list.published_at ? new Date(list.published_at).toLocaleDateString(en ? "en-GB" : "cs") : ""}
                            {list.criteria?.gw_start && <> · GW{list.criteria.gw_start}–{list.criteria.gw_end}</>}
                            {list.criteria?.budget_max != null && <> · {en ? "up to" : "do"} £{list.criteria.budget_max}</>}
                        </div>
                        <h1 className="font-display font-black text-3xl uppercase tracking-tight" data-testid="focus-list-title">{title}</h1>
                        <div className="flex items-center gap-2 font-mono text-[10px] text-white/55 flex-wrap" data-testid="focus-list-author">
                            <span>{T.curated} <span className="text-fa-sky font-bold">{list.author?.name}</span></span>
                            <span className="border border-white/15 px-1.5 py-0.5 text-[9px]">{T.rep} {list.author?.reputation ?? 0}</span>
                            {list.author_score && (
                                <span className="text-fa-yellow text-[9px]">{T.resolved}: {list.author_score.hits}/{list.author_score.total} {T.hits}</span>
                            )}
                        </div>
                        {intro && <p className="text-white/60 text-base italic border-l-2 border-fa-yellow pl-3">{intro}</p>}
                        <p className="font-mono text-[10px] text-white/50 border border-white/10 bg-black/30 px-3 py-2">{T.explain()}</p>
                        <div className="space-y-3">
                            {list.players.map((p, i) => (
                                <PlayerCard key={p.fpl_id} slug={slug} p={p} rank={i + 1} agg={votes} onVoted={loadVotes} T={T} note={noteFor(p)}
                                            res={list.author_score?.details?.[String(p.fpl_id)]} />
                            ))}
                        </div>
                        <p className="font-mono text-[9px] text-white/35 leading-relaxed">{T.footer}</p>
                    </article>
                )}
            </div>
        </AppShell>
    );
}
